import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, SelectChangeEvent } from '@mui/material';
import { PatientSearchForm, PatientTable } from '../components/patient';
import { CSVExportParams, getPatients, getPatientsCSVPreview } from '../api/patients';
import { BlockedStatus, Patient, PatientSearchParams, ResignedStatus, TalkStatus, TestAccountStatus } from '../types/patient';
import { CSVExportDialog } from '../components/common';
import { useCSVExport } from '../hooks/useCSVExport';
import { useAuth } from '../hooks/useAuth';
import { useLocation } from 'react-router-dom';
// ローカルストレージのキー定義
const STORAGE_KEY = 'patient_list_search';
const PatientList: React.FC = () => {
  // 認証情報を取得
  const { user, isDeveloper } = useAuth();
  // ロケーション情報を取得（ダッシュボードからの遷移パラメータ用）
  const location = useLocation();
  const locationState = location.state as {
    createdAtStart?: string,
    createdAtEnd?: string,
    resignedStatus?: string,
    talkStatus?: string,
    testAccountStatus?: string
  } | null;
  // データ表示用の状態 
  const [ patients, setPatients ] = useState<Patient[]>([]);
  const [ totalCount, setTotalCount ] = useState(0);
  const [ loading, setLoading ] = useState(false);
  const [ error, setError ] = useState<string | null>(null);
  // リクエスト重複防止用のref
  const requestInProgressRef = useRef<{ [key: string]: boolean }>({});
  // 検索条件を取得する関数
  const getSavedFilters = () => {
    try {
      const savedState = localStorage.getItem(STORAGE_KEY);
      if (savedState) {
        return JSON.parse(savedState);
      }
    } catch (e) {
      console.error('検索条件の復元に失敗:', e);
      localStorage.removeItem(STORAGE_KEY);
    }
    return null;
  };
  // 保存された状態または初期値で状態を初期化 - useMemoで評価を最適化
  const savedFilters = React.useMemo(() => getSavedFilters(), []);
  // フォーム入力値（ユーザーの入力を保持）
  const [ searchTerm, setSearchTerm ] = useState(savedFilters?.searchTerm || '');
  const [ emailSearchTerm, setEmailSearchTerm ] = useState(savedFilters?.emailSearchTerm || '');
  const [ userIdSearchTerm, setUserIdSearchTerm ] = useState(savedFilters?.userIdSearchTerm || '');
  const [ selectedRoles, setSelectedRoles ] = useState<string[]>(savedFilters?.selectedRoles || []);
  const [ resignedStatus, setResignedStatus ] = useState<string>(locationState?.resignedStatus || savedFilters?.resignedStatus || ResignedStatus.ACTIVE_ONLY);
  const [ talkStatus, setTalkStatus ] = useState<string>(locationState?.talkStatus || savedFilters?.talkStatus || TalkStatus.ALL);
  const [ blockedStatus, setBlockedStatus ] = useState<string>(savedFilters?.blockedStatus || BlockedStatus.ALL);
  const [ conversationCount, setConversationCount ] = useState(savedFilters?.conversationCount || '');
  const [ conversationCountOperator, setConversationCountOperator ] = useState(savedFilters?.conversationCountOperator || 'eq'); // eq, gte, lte
  const [ testAccountStatus, setTestAccountStatus ] = useState<string>(locationState?.testAccountStatus || savedFilters?.testAccountStatus || TestAccountStatus.ALL);
  // 登録日範囲検索用フィルター
  const [ createdAtStart, setCreatedAtStart ] = useState<string | null>(locationState?.createdAtStart || savedFilters?.createdAtStart || null);
  const [ createdAtEnd, setCreatedAtEnd ] = useState<string | null>(locationState?.createdAtEnd || savedFilters?.createdAtEnd || null);
  // ダッシュボードからの遷移時は初期検索パラメータを設定
  const initialSearchParams = locationState ? {
    searchTerm: '',
    emailSearchTerm: '',
    userIdSearchTerm: '',
    selectedRoles: [] as string[],
    resignedStatus: locationState.resignedStatus || ResignedStatus.ACTIVE_ONLY,
    talkStatus: locationState.talkStatus || TalkStatus.ALL,
    blockedStatus: BlockedStatus.ALL,
    conversationCount: '',
    conversationCountOperator: 'eq',
    testAccountStatus: locationState.testAccountStatus || TestAccountStatus.ALL,
    createdAtStart: locationState.createdAtStart || null,
    createdAtEnd: locationState.createdAtEnd || null
  } : null;
  // 実際の検索条件（検索ボタン押下時に更新）
  const [ searchParams, setSearchParams ] = useState(initialSearchParams || {
    searchTerm: savedFilters?.searchTerm || '',
    emailSearchTerm: savedFilters?.emailSearchTerm || '',
    userIdSearchTerm: savedFilters?.userIdSearchTerm || '',
    selectedRoles: savedFilters?.selectedRoles || [] as string[],
    resignedStatus: savedFilters?.resignedStatus || ResignedStatus.ACTIVE_ONLY,
    talkStatus: savedFilters?.talkStatus || TalkStatus.ALL,
    blockedStatus: savedFilters?.blockedStatus || BlockedStatus.ALL,
    conversationCount: savedFilters?.conversationCount || '',
    conversationCountOperator: savedFilters?.conversationCountOperator || 'eq',
    testAccountStatus: savedFilters?.testAccountStatus || TestAccountStatus.ALL,
    createdAtStart: savedFilters?.createdAtStart || null,
    createdAtEnd: savedFilters?.createdAtEnd || null
  });
  // ページネーション用の状態
  const [ page, setPage ] = useState(savedFilters?.page || 0);
  const [ rowsPerPage, setRowsPerPage ] = useState(savedFilters?.rowsPerPage || 20);
  // CSV出力ダイアログの状態
  const [ csvDialogOpen, setCSVDialogOpen ] = useState(false);
  const [ csvPreviewData, setCSVPreviewData ] = useState<any>(null);
  // CSV出力フック
  const { exportCSV, loading: csvLoading, error: csvError } = useCSVExport({
    endpoint: '/external/patients',
  });
  // 患者データを取得する関数 - useCallbackでメモ化
  const fetchPatients = React.useCallback(async(params: PatientSearchParams) => {
    // リクエストキーを生成（クエリパラメータ文字列）
    const requestKey = JSON.stringify(params);
    // 同一パラメータのリクエストが進行中なら実行しない
    if (requestInProgressRef.current[requestKey]) {
      return;
    }
    // リクエスト開始を記録
    requestInProgressRef.current[requestKey] = true;
    setLoading(true);
    setError(null);
    try {
      // APIパラメータ準備
      const apiParams = {
        ...params,
        page: params.page ? params.page + 1 : 1, // APIは1-indexedだがMUIは0-indexed
        page_size: params.page_size // 明示的にpage_sizeを指定
      };
      const data = await getPatients(apiParams);
      // APIからの結果をセット
      if (data && data.results) {
        // シリアライザーが対話状態を含むようになったので、APIから返ってきた値をそのまま使用
        const processedPatients = data.results;
        setPatients(processedPatients);
        setTotalCount(data.count);
      } else {
        // API結果がない場合は空の配列をセット
        setPatients([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('患者データの取得に失敗しました', err);
      setError('患者データの取得に失敗しました。外部DBの接続設定を確認してください。');
      // エラー時は空の配列をセット
      setPatients([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
      // リクエスト完了を記録
      requestInProgressRef.current[requestKey] = false;
    }
  }, []);
  // ダッシュボードから遷移してきた場合、前の検索条件をリセットして新しい条件を適用する
  useEffect(() => {
    if (locationState) {
      // 検索フィールドの値をリセット
      setSearchTerm('');
      setEmailSearchTerm('');
      setUserIdSearchTerm('');
      setSelectedRoles([]);
      setTalkStatus(locationState.talkStatus || TalkStatus.ALL);
      setBlockedStatus(BlockedStatus.ALL);
      setConversationCount('');
      setConversationCountOperator('eq');
      setTestAccountStatus(locationState.testAccountStatus || TestAccountStatus.ALL);
      // ロケーションステートから値を取得してセット
      setResignedStatus(locationState.resignedStatus || ResignedStatus.ACTIVE_ONLY);
      setCreatedAtStart(locationState.createdAtStart || null);
      setCreatedAtEnd(locationState.createdAtEnd || null);
      // 検索パラメータも更新
      setSearchParams({
        searchTerm: '',
        emailSearchTerm: '',
        userIdSearchTerm: '',
        selectedRoles: [],
        resignedStatus: locationState.resignedStatus || ResignedStatus.ACTIVE_ONLY,
        talkStatus: locationState.talkStatus || TalkStatus.ALL,
        blockedStatus: BlockedStatus.ALL,
        conversationCount: '',
        conversationCountOperator: 'eq',
        testAccountStatus: locationState.testAccountStatus || TestAccountStatus.ALL,
        createdAtStart: locationState.createdAtStart || null,
        createdAtEnd: locationState.createdAtEnd || null
      });
      // ページをリセット
      setPage(0);
      // localStorageから古い検索条件を削除
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [ locationState ]);
  // 検索パラメータ変更時にデータ取得およびローカルストレージ保存
  useEffect(() => {
    // 現在の状態をローカルストレージに保存
    const currentState = {
      searchTerm: searchParams.searchTerm,
      emailSearchTerm: searchParams.emailSearchTerm,
      userIdSearchTerm: searchParams.userIdSearchTerm,
      page,
      rowsPerPage,
      selectedRoles: searchParams.selectedRoles,
      resignedStatus: searchParams.resignedStatus,
      talkStatus: searchParams.talkStatus,
      blockedStatus: searchParams.blockedStatus,
      conversationCount: searchParams.conversationCount,
      conversationCountOperator: searchParams.conversationCountOperator,
      testAccountStatus: searchParams.testAccountStatus,
      createdAtStart: searchParams.createdAtStart,
      createdAtEnd: searchParams.createdAtEnd
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(currentState));
    // API用パラメータ作成
    const params: PatientSearchParams = {
      page,
      page_size: rowsPerPage,
      search: searchParams.searchTerm || undefined,
      email_search: searchParams.emailSearchTerm || undefined,
      include_resigned: searchParams.resignedStatus
    };
    // 開発者モードの場合はuser_idによる検索を追加
    if (isDeveloper() && searchParams.userIdSearchTerm) {
      params.user_id = searchParams.userIdSearchTerm;
    }
    // 対話状態フィルタの追加
    if (searchParams.talkStatus && searchParams.talkStatus !== TalkStatus.ALL) {
      params.talk_status = searchParams.talkStatus;
    }
    // ブロック状態フィルタの追加
    if (searchParams.blockedStatus && searchParams.blockedStatus !== BlockedStatus.ALL) {
      params.blocked_status = searchParams.blockedStatus;
    }
    // ロールフィルターの追加（値がある場合のみ）
    if (Array.isArray(searchParams.selectedRoles) && searchParams.selectedRoles.length > 0) {
      params.role_type = searchParams.selectedRoles;
    }
    // 対話回数フィルターの追加
    if (searchParams.conversationCount && searchParams.conversationCount.trim() !== '') {
      const count = searchParams.conversationCount.trim();
      // 演算子に基づいて適切なパラメータ名を設定
      if (searchParams.conversationCountOperator === 'eq') {
        params.conversation_count = count;
      } else if (searchParams.conversationCountOperator === 'gte') {
        params.conversation_count_gte = count;
      } else if (searchParams.conversationCountOperator === 'lte') {
        params.conversation_count_lte = count;
      }
      // どの演算子が使用されたかも送信
      params.conversation_count_operator = searchParams.conversationCountOperator;
    }
    // 登録日範囲フィルターの追加
    // 登録日(開始)だけでも検索できる
    if (searchParams.createdAtStart) {
      params.created_at_start = searchParams.createdAtStart;
      // 終了日が設定されている場合のみ追加（終了日単体での検索はない）
      if (searchParams.createdAtEnd) {
        params.created_at_end = searchParams.createdAtEnd;
      }
    }
    // テストアカウントフィルターの追加
    if (searchParams.testAccountStatus && searchParams.testAccountStatus !== TestAccountStatus.ALL) {
      params.is_test_account = searchParams.testAccountStatus;
    }
    // APIリクエスト実行
    fetchPatients(params);
  }, [ page, rowsPerPage, searchParams, fetchPatients ]);
  // ページ変更ハンドラ
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };
  // 1ページあたりの表示件数変更ハンドラ
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(event.target.value, 10);
    setRowsPerPage(newValue);
    setPage(0);
  };
  // 検索実行関数
  const handleSearch = () => {
    // 検索条件を更新
    setSearchParams({
      searchTerm,
      emailSearchTerm,
      userIdSearchTerm,
      selectedRoles,
      resignedStatus,
      talkStatus,
      blockedStatus,
      conversationCount,
      conversationCountOperator,
      testAccountStatus,
      createdAtStart,
      createdAtEnd
    });
    setPage(0); // ページをリセット
  };
  // 検索条件をクリア
  const handleClearFilters = () => {
    setSearchTerm('');
    setEmailSearchTerm('');
    setUserIdSearchTerm('');
    setSelectedRoles([]);
    setResignedStatus(ResignedStatus.ACTIVE_ONLY);
    setTalkStatus(TalkStatus.ALL);
    setBlockedStatus(BlockedStatus.ALL);
    setConversationCount('');
    setConversationCountOperator('eq');
    setTestAccountStatus(TestAccountStatus.ALL);
    setCreatedAtStart(null);
    setCreatedAtEnd(null);
    // 検索条件も即時クリア
    setSearchParams({
      searchTerm: '',
      emailSearchTerm: '',
      userIdSearchTerm: '',
      selectedRoles: [],
      resignedStatus: ResignedStatus.ACTIVE_ONLY,
      talkStatus: TalkStatus.ALL,
      blockedStatus: BlockedStatus.ALL,
      conversationCount: '',
      conversationCountOperator: 'eq',
      testAccountStatus: TestAccountStatus.ALL,
      createdAtStart: null,
      createdAtEnd: null
    });
    setPage(0);
    localStorage.removeItem(STORAGE_KEY);
  };
  // フォーム送信ハンドラ
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    handleSearch();
  };
  // 名前/ニックネーム検索フィールドクリアハンドラ
  const handleClearSearchField = () => {
    setSearchTerm('');
  };
  // メールアドレス検索フィールドクリアハンドラ
  const handleClearEmailField = () => {
    setEmailSearchTerm('');
  };
  // 開発者モード用 user_id 検索フィールドクリアハンドラ
  const handleClearUserIdField = () => {
    setUserIdSearchTerm('');
  };
  // 開発者モード用 user_id 検索入力ハンドラ
  const handleUserIdSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserIdSearchTerm(e.target.value);
  };
  // ロール選択ハンドラ
  const handleRoleChange = (event: any) => {
    const value = event.target.value as string[];
    setSelectedRoles(value);
  };
  // 退会状態表示切替ハンドラ
  const handleResignedStatusChange = (event: SelectChangeEvent) => {
    setResignedStatus(event.target.value);
  };
  // 対話状態表示切替ハンドラ
  const handleTalkStatusChange = (event: SelectChangeEvent) => {
    setTalkStatus(event.target.value);
  };
  // ブロック状態表示切替ハンドラ
  const handleBlockedStatusChange = (event: SelectChangeEvent) => {
    setBlockedStatus(event.target.value);
  };
  // 対話回数入力ハンドラ
  const handleConversationCountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // 数値のみを許可
    const value = event.target.value.replace(/[^0-9]/g, '');
    setConversationCount(value);
  };
  // 対話回数演算子変更ハンドラ
  const handleConversationCountOperatorChange = (event: SelectChangeEvent) => {
    setConversationCountOperator(event.target.value);
  };
  // テストアカウント状態表示切替ハンドラ
  const handleTestAccountStatusChange = (event: SelectChangeEvent) => {
    setTestAccountStatus(event.target.value);
  };
  // CSV出力ハンドラ
  const handleCSVExport = async() => {
    try {
      // 一覧表示と同じAPIパラメータ形式を作成
      const params: PatientSearchParams = {
        search: searchParams.searchTerm || undefined,
        email_search: searchParams.emailSearchTerm || undefined,
        user_id: searchParams.userIdSearchTerm || undefined,
      };
      // ロールフィルターの追加
      if (searchParams.selectedRoles && searchParams.selectedRoles.length > 0) {
        params.role_type = searchParams.selectedRoles;
      }
      // 対話状態フィルターの追加
      if (searchParams.talkStatus && searchParams.talkStatus !== TalkStatus.ALL) {
        params.talk_status = searchParams.talkStatus;
      }
      // ブロック状態フィルターの追加
      if (searchParams.blockedStatus && searchParams.blockedStatus !== BlockedStatus.ALL) {
        params.blocked_status = searchParams.blockedStatus;
      }
      // 対話回数フィルターの追加
      if (searchParams.conversationCount && searchParams.conversationCount.trim() !== '') {
        const count = searchParams.conversationCount.trim();
        if (searchParams.conversationCountOperator === 'eq') {
          params.conversation_count = count;
        } else if (searchParams.conversationCountOperator === 'gte') {
          params.conversation_count_gte = count;
        } else if (searchParams.conversationCountOperator === 'lte') {
          params.conversation_count_lte = count;
        }
      }
      // 登録日範囲フィルターの追加
      if (searchParams.createdAtStart) {
        params.created_at_start = searchParams.createdAtStart;
        if (searchParams.createdAtEnd) {
          params.created_at_end = searchParams.createdAtEnd;
        }
      }
      // 退会状態フィルターの追加
      if (searchParams.resignedStatus) {
        params.include_resigned = searchParams.resignedStatus;
      }
      // プレビューデータを取得
      const preview = await getPatientsCSVPreview(params);
      setCSVPreviewData(preview);
      setCSVDialogOpen(true);
    } catch (error) {
      console.error('CSV preview error:', error);
    }
  };
  // CSV出力実行
  const handleCSVExportConfirm = async(selectedOptions: string[], excludeFields: string[]) => {
    try {
      // 一覧表示と同じAPIパラメータ形式を作成（プレビューと同じ）
      const params: CSVExportParams = {
        search: searchParams.searchTerm || undefined,
        email_search: searchParams.emailSearchTerm || undefined,
        user_id: searchParams.userIdSearchTerm || undefined,
        exclude_fields: excludeFields,
      };
      // ロールフィルターの追加
      if (searchParams.selectedRoles && searchParams.selectedRoles.length > 0) {
        params.role_type = searchParams.selectedRoles;
      }
      // 対話状態フィルターの追加
      if (searchParams.talkStatus && searchParams.talkStatus !== TalkStatus.ALL) {
        params.talk_status = searchParams.talkStatus;
      }
      // ブロック状態フィルターの追加
      if (searchParams.blockedStatus && searchParams.blockedStatus !== BlockedStatus.ALL) {
        params.blocked_status = searchParams.blockedStatus;
      }
      // 対話回数フィルターの追加
      if (searchParams.conversationCount && searchParams.conversationCount.trim() !== '') {
        const count = searchParams.conversationCount.trim();
        if (searchParams.conversationCountOperator === 'eq') {
          params.conversation_count = count;
        } else if (searchParams.conversationCountOperator === 'gte') {
          params.conversation_count_gte = count;
        } else if (searchParams.conversationCountOperator === 'lte') {
          params.conversation_count_lte = count;
        }
      }
      // 登録日範囲フィルターの追加
      if (searchParams.createdAtStart) {
        params.created_at_start = searchParams.createdAtStart;
        if (searchParams.createdAtEnd) {
          params.created_at_end = searchParams.createdAtEnd;
        }
      }
      // 退会状態フィルターの追加
      if (searchParams.resignedStatus) {
        params.include_resigned = searchParams.resignedStatus;
      }
      // テストアカウントフィルターの追加
      if (searchParams.testAccountStatus && searchParams.testAccountStatus !== TestAccountStatus.ALL) {
        params.is_test_account = searchParams.testAccountStatus;
      }
      await exportCSV(params);
    } catch (error) {
      // エラーはuseCSVExport内で処理される
    }
  };
  return (
    <Box>
      {/* 検索フォーム */}
      <PatientSearchForm
        searchTerm={searchTerm}
        onSearchTermChange={(e) => setSearchTerm(e.target.value)}
        onClearSearchField={handleClearSearchField}
        emailSearchTerm={emailSearchTerm}
        onEmailSearchTermChange={(e) => setEmailSearchTerm(e.target.value)}
        onClearEmailField={handleClearEmailField}
        userIdSearchTerm={userIdSearchTerm}
        onUserIdSearchTermChange={handleUserIdSearchTermChange}
        onClearUserIdField={handleClearUserIdField}
        selectedRoles={selectedRoles}
        onRoleChange={handleRoleChange}
        talkStatus={talkStatus}
        onTalkStatusChange={handleTalkStatusChange}
        blockedStatus={blockedStatus}
        onBlockedStatusChange={handleBlockedStatusChange}
        resignedStatus={resignedStatus}
        onResignedStatusChange={handleResignedStatusChange}
        conversationCount={conversationCount}
        onConversationCountChange={handleConversationCountChange}
        conversationCountOperator={conversationCountOperator}
        onConversationCountOperatorChange={handleConversationCountOperatorChange}
        testAccountStatus={testAccountStatus}
        onTestAccountStatusChange={handleTestAccountStatusChange}
        createdAtStart={createdAtStart}
        onCreatedAtStartChange={setCreatedAtStart}
        createdAtEnd={createdAtEnd}
        onCreatedAtEndChange={setCreatedAtEnd}
        totalCount={totalCount}
        onSearch={handleSubmit}
        onClear={handleClearFilters}
        onCSVExport={user?.role === 'ADMIN' ? handleCSVExport : undefined}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* 患者一覧テーブル */}
      <PatientTable
        patients={patients}
        loading={loading}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        searchParams={searchParams}
      />

      {/* CSV出力ダイアログ */}
      <CSVExportDialog
        open={csvDialogOpen}
        onClose={() => setCSVDialogOpen(false)}
        title="患者情報 ダウンロード"
        onExport={handleCSVExportConfirm}
        previewData={csvPreviewData ? {
          headers: csvPreviewData.headers,
          all_headers: csvPreviewData.all_headers,
          personal_fields: csvPreviewData.personal_fields,
          rows: csvPreviewData.rows,
          totalCount: csvPreviewData.total_count
        } : undefined}
        loading={csvLoading}
        error={csvError}
      />
    </Box>
  );
};
export default PatientList;
